import { useEffect, useRef, useState } from 'react';

export interface SizeChip {
  key: string;
  /** "1 bar (55 g)". Long labels get an ellipsis; the detail never does. */
  label: string;
  /** After a dot: the size's calories ("150 kcal"), or the amount of a "Last time" chip. */
  detail?: string | null;
  /** The amount set is this size. Leave out where there is no amount yet (search results). */
  active?: boolean;
  /** Set apart from the sizes, like "Last time · 65 g". */
  lead?: boolean;
  onPick: () => void;
}

interface Props {
  chips: SizeChip[];
  /** How many show before a "+N more" chip. It never hides just one: the chip would take that one's place. */
  limit: number;
  /** Names the group for screen readers. */
  label: string;
  small?: boolean;
}

/** Sizes as one-tap chips, each with its calories, and the rest behind "+N more". */
export function SizeChips({ chips, limit, label, small = false }: Props) {
  // A chosen size that would be hidden starts out shown, so it can be seen as chosen.
  const [expanded, setExpanded] = useState(() => chips.slice(limit).some((c) => c.active));
  const firstRevealed = useRef<HTMLButtonElement>(null);
  const focusRevealed = useRef(false);

  // "+N more" goes away once used: focus moves on to the first size it showed.
  useEffect(() => {
    if (!expanded || !focusRevealed.current) return;
    focusRevealed.current = false;
    firstRevealed.current?.focus();
  }, [expanded]);

  if (chips.length === 0) return null;
  const hidden = expanded || chips.length <= limit + 1 ? 0 : chips.length - limit;
  const shown = hidden > 0 ? chips.slice(0, limit) : chips;
  const size = small ? ' chip--sm' : '';
  return (
    <div className="servings" role="group" aria-label={label}>
      {shown.map((c, i) => (
        <button
          type="button"
          key={c.key}
          ref={i === limit ? firstRevealed : undefined}
          className={`chip chip--size${size}${c.lead ? ' chip--accent' : ''}${c.active ? ' is-active' : ''}`}
          aria-pressed={c.active}
          onClick={c.onPick}
        >
          <span className="chip__label">{c.label}</span>
          {c.detail && (
            <span className="chip__detail num">
              {' '}
              <span aria-hidden="true">·</span> {c.detail}
            </span>
          )}
        </button>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className={`chip chip--more${size}`}
          onClick={() => {
            focusRevealed.current = true;
            setExpanded(true);
          }}
        >
          +{hidden} more<span className="sr-only"> sizes</span>
        </button>
      )}
    </div>
  );
}
